import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  FileSpreadsheet,
  Upload,
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Download,
} from 'lucide-react';
import excelRoundtripService from '../services/excelRoundtripService';

const ENTITY_LABELS = {
  purchase_order: '采购单',
  quotation: '报价单',
  outbound: '出库单',
};

// 变更预览中的字段名 -> 中文标签（与导出 Excel 的列头一致）
const FIELD_LABELS = {
  product_code: '产品编码',
  product_name: '产品名称',
  specification: '规格',
  unit: '单位',
  quantity: '数量',
  unit_price: '单价',
  tax_rate: '税率',
  location: '库位',
  notes: '备注',
};

const STEPS = [
  { key: 'export', label: '导出 Excel' },
  { key: 'preview', label: '上传与预览' },
  { key: 'result', label: '应用结果' },
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-2 px-6 py-3 border-b bg-muted/30">
    {STEPS.map((s, idx) => {
      const currentIdx = STEPS.findIndex((x) => x.key === currentStep);
      const isActive = s.key === currentStep;
      const isDone = idx < currentIdx;
      return (
        <React.Fragment key={s.key}>
          {idx > 0 && <div className="h-px w-6 bg-border" />}
          <div className="flex items-center gap-1.5">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : isDone
                    ? 'bg-success/20 text-success'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {isDone ? <CheckCircle className="h-3.5 w-3.5" /> : idx + 1}
            </span>
            <span className={`text-sm ${isActive ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
              {s.label}
            </span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

const countChanges = (doc) => {
  const c = doc.changes || {};
  return (c.modified?.length || 0) + (c.added?.length || 0) + (c.deleted?.length || 0);
};

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

/**
 * Excel 批量编辑三步向导：导出 -> 上传解析预览 -> 确认应用
 *
 * @param {boolean} open
 * @param {Function} onOpenChange
 * @param {string} entityType - 'purchase_order' | 'quotation' | 'outbound'
 * @param {Array<string>} selectedIds - 要导出的单据 id（当前列表筛选结果）
 * @param {Function} onApplied - 应用成功后回调，父页面用于刷新列表
 */
const ExcelRoundtripDialog = ({ open, onOpenChange, entityType, selectedIds = [], onApplied }) => {
  const [step, setStep] = useState('export');
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);

  const entityLabel = ENTITY_LABELS[entityType] || entityType;

  const resetDialog = () => {
    setStep('export');
    setExporting(false);
    setExported(false);
    setFile(null);
    setParsing(false);
    setParseResult(null);
    setApplying(false);
    setApplyResult(null);
    setError(null);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const { blob, filename } = await excelRoundtripService.exportRoundtrip(entityType, selectedIds);
      excelRoundtripService.downloadBlob(blob, filename);
      setExported(true);
    } catch (err) {
      setError(err.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleParse = async () => {
    if (!file) {
      setError('请先选择文件');
      return;
    }
    setParsing(true);
    setError(null);
    try {
      const result = await excelRoundtripService.parseRoundtrip(entityType, file);
      setParseResult(result);
    } catch (err) {
      setError(err.message || '解析失败');
    } finally {
      setParsing(false);
    }
  };

  const applicableDocs = parseResult
    ? parseResult.documents.filter((d) => !(d.errors?.length) && countChanges(d) > 0)
    : [];
  const errorDocCount = parseResult
    ? parseResult.documents.filter((d) => d.errors?.length > 0).length
    : 0;

  const handleApply = async () => {
    if (applicableDocs.length === 0 || !parseResult?.rawChanges) return;
    setApplying(true);
    setError(null);
    try {
      // 服务端会基于 DB 当前值重新校验并跳过有错误的单据，
      // 因此把 parse 返回的完整 changes 原样回传
      const result = await excelRoundtripService.applyRoundtrip(entityType, parseResult.rawChanges);
      setApplyResult(result);
      setStep('result');
      if (result.failed.length === 0 && onApplied) {
        onApplied(result);
      }
    } catch (err) {
      setError(err.message || '应用变更失败');
    } finally {
      setApplying(false);
    }
  };

  const handleResultClose = () => {
    // Refresh parent list even on partial failure — some docs were applied
    if (applyResult && applyResult.success.length > 0 && onApplied) {
      onApplied(applyResult);
    }
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }} modal={true}>
      <DialogContent
        className="max-w-[1000px] w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle>Excel 批量编辑 — {entityLabel}</DialogTitle>
          <DialogDescription>
            导出当前列表的{entityLabel}，在 Excel 中修改后上传，确认无误后批量应用。
          </DialogDescription>
        </DialogHeader>

        <StepIndicator currentStep={step} />

        {step === 'export' && (
          <div className="flex-1 overflow-auto p-6">
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>导出后请<strong>不要删除或修改</strong>隐藏的 <code className="font-mono">_meta</code> sheet，系统靠它识别行项目。</li>
                  <li>要删除某个行项目，请在该行的<strong>「删除标记」列填"是"</strong>，不要直接删除整行。</li>
                  <li>要新增行项目，直接<strong>插入新行并填写产品编码</strong>即可。</li>
                  <li>已确认/已完成状态的单据不可修改，解析时会被标记为错误并跳过。</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-10 text-center space-y-4">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-green-600" />
              <div className="text-lg font-medium">
                导出当前列表 {selectedIds.length} 张{entityLabel}
              </div>
              <div className="text-sm text-muted-foreground">
                下载后在 Excel 中编辑，再回到本向导上传
              </div>
              <Button onClick={handleExport} disabled={exporting || selectedIds.length === 0} className="gap-2">
                {exporting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    正在导出...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    导出 Excel
                  </>
                )}
              </Button>
              {exported && (
                <div className="text-sm text-success flex items-center justify-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  导出成功，文件已开始下载
                </div>
              )}
            </div>

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={handleClose}>
                取消
              </Button>
              <Button onClick={() => { setError(null); setStep('preview'); }} className="gap-2">
                下一步：上传文件
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b bg-muted/30 flex items-center justify-between gap-4">
              <Button variant="ghost" size="sm" onClick={() => setStep('export')} className="gap-1">
                <ArrowLeft className="h-4 w-4" />
                上一步
              </Button>
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="roundtrip-file-upload"
                />
                {file ? (
                  <div className="flex items-center gap-2 text-sm">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    <span className="font-medium">{file.name}</span>
                    <span className="text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                    <Button variant="ghost" size="sm" asChild>
                      <label htmlFor="roundtrip-file-upload" className="cursor-pointer">更换</label>
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" asChild>
                    <label htmlFor="roundtrip-file-upload" className="cursor-pointer gap-1">
                      <Upload className="h-4 w-4 mr-1" />
                      选择修改后的 .xlsx 文件
                    </label>
                  </Button>
                )}
              </div>
              <Button onClick={handleParse} disabled={!file || parsing} size="sm" className="gap-2">
                {parsing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    正在解析...
                  </>
                ) : (
                  '解析变更'
                )}
              </Button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {!parseResult && !parsing && (
                <div className="text-center text-muted-foreground py-12">
                  选择文件并点击「解析变更」查看将要应用的修改
                </div>
              )}

              {parseResult && (
                <>
                  {parseResult.errors.length > 0 && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-medium mb-1">文件级错误：</div>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {parseResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {parseResult.documents.length === 0 && (
                    <div className="text-center text-muted-foreground py-12">
                      未检测到任何变更
                    </div>
                  )}

                  {parseResult.documents.map((doc) => {
                    const changes = countChanges(doc);
                    const hasErrors = doc.errors?.length > 0;
                    return (
                      <div key={doc.doc_id || doc.doc_number} className="border rounded-lg mb-4 overflow-hidden">
                        <div className={`flex items-center justify-between px-4 py-2 ${hasErrors ? 'bg-destructive/10' : 'bg-muted/50'}`}>
                          <div className="flex items-center gap-3">
                            <span className="font-medium">{doc.doc_number || doc.doc_id}</span>
                            {doc.client_name && (
                              <span className="text-sm text-muted-foreground">{doc.client_name}</span>
                            )}
                            {doc.status && <Badge variant="outline">{doc.status}</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            {hasErrors ? (
                              <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                将被跳过
                              </Badge>
                            ) : changes > 0 ? (
                              <Badge variant="secondary">{changes} 项变更</Badge>
                            ) : (
                              <Badge variant="outline">无变更</Badge>
                            )}
                          </div>
                        </div>

                        {hasErrors && (
                          <div className="px-4 py-2 text-sm text-destructive border-b">
                            {doc.errors.map((e, i) => <div key={i}>• {e}</div>)}
                          </div>
                        )}

                        {changes > 0 && (
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/30">
                                <TableHead className="w-16">行号</TableHead>
                                <TableHead className="w-20">类型</TableHead>
                                <TableHead>内容</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {doc.changes.modified.map((m, i) => (
                                <TableRow key={`m-${i}`}>
                                  <TableCell className="font-mono text-sm">{formatValue(m.row)}</TableCell>
                                  <TableCell><Badge variant="secondary">修改</Badge></TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      {m.fields.map((f, j) => (
                                        <div key={j} className="text-sm">
                                          <span className="font-medium">{FIELD_LABELS[f.field] || f.field}</span>
                                          <span className="text-muted-foreground">: {formatValue(f.old)}</span>
                                          <span className="mx-1 text-muted-foreground">→</span>
                                          <span className="font-medium text-primary bg-primary/10 px-1 rounded">
                                            {formatValue(f.new)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {doc.changes.added.map((a, i) => (
                                <TableRow key={`a-${i}`} className="bg-success/5">
                                  <TableCell className="font-mono text-sm">{formatValue(a.row)}</TableCell>
                                  <TableCell><Badge variant="success">新增</Badge></TableCell>
                                  <TableCell className="text-sm text-success">
                                    {a.product_code} {a.name}
                                    {a.quantity !== undefined && a.quantity !== null && ` × ${a.quantity}`}
                                    {a.unit_price !== undefined && a.unit_price !== null && ` @ ${a.unit_price}`}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {doc.changes.deleted.map((d, i) => (
                                <TableRow key={`d-${i}`} className="bg-destructive/5">
                                  <TableCell className="font-mono text-sm">{formatValue(d.row)}</TableCell>
                                  <TableCell><Badge variant="destructive">删除</Badge></TableCell>
                                  <TableCell className="text-sm text-destructive line-through">
                                    {d.product_code} {d.name}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {parseResult ? (
                  <>
                    共 {parseResult.documents.length} 张单据，可应用 {applicableDocs.length} 张
                    {errorDocCount > 0 && (
                      <span className="text-destructive font-medium ml-2">
                        {errorDocCount} 张单据有错误将被跳过
                      </span>
                    )}
                  </>
                ) : (
                  '请先解析文件'
                )}
              </div>
              <Button onClick={handleApply} disabled={applicableDocs.length === 0 || applying} className="gap-2">
                {applying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    正在应用...
                  </>
                ) : (
                  <>
                    确认应用变更
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && applyResult && (
          <div className="flex-1 overflow-auto p-6">
            <div className="text-center space-y-4 mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
                applyResult.failed.length === 0 ? 'bg-success/10' : 'bg-warning/10'
              }`}>
                {applyResult.failed.length === 0 ? (
                  <CheckCircle className="h-8 w-8 text-success" />
                ) : (
                  <AlertTriangle className="h-8 w-8 text-warning" />
                )}
              </div>
              <div className="text-xl font-medium">
                成功 {applyResult.success.length} 张，失败 {applyResult.failed.length} 张
              </div>
            </div>

            {applyResult.success.length > 0 && (
              <div className="border rounded-lg mb-4 overflow-hidden">
                <div className="px-4 py-2 bg-success/10 font-medium text-sm text-success">
                  已成功应用（{applyResult.success.length}）
                </div>
                <div className="px-4 py-2 space-y-1">
                  {applyResult.success.map((s, i) => (
                    <div key={i} className="text-sm flex items-center gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-success" />
                      {s.doc_number || s.doc_id}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {applyResult.failed.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-destructive/10 font-medium text-sm text-destructive">
                  应用失败（{applyResult.failed.length}）
                </div>
                <div className="px-4 py-2 space-y-1">
                  {applyResult.failed.map((f, i) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium">{f.doc_number || f.doc_id}</span>
                      <span className="text-destructive ml-2">{f.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <Button onClick={handleResultClose}>完成</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExcelRoundtripDialog;
